import { FeatureServiceBinder, FeatureServiceProviderDefinition, SharedFeatureService } from '@feature-hub/core';
import { ServiceConfigProviderV1, ServiceConfigurationV1 } from '@volkswagen-onehub/service-config-provider';
import { BehaviorSubject, catchError, from, map, Observable, of } from 'rxjs';
import { CalculatorProduct } from '../../hooks/calculator/use-calculator';
import { fetchDataCalculator } from './api/calculator-api';
import { checkTenant, getDataUrl } from './data-url/get-data-url';
import { CarlineConfiguration, Configuration } from './models/calculator';
import { CarlineWithOfertas, filtersFaContentOfertas, FilterTypes, Oferta } from './models/ofertas';
import { OfertasV2 } from './v2';

export interface SharedStarterFeatureService extends SharedFeatureService {
	readonly '2.0.0': FeatureServiceBinder<OfertasV2>;
}

export const ofertasServiceDefinition: FeatureServiceProviderDefinition<SharedStarterFeatureService> = {
	id: 'es:ofertas-feature-service',
	dependencies: { featureServices: { 'service-config-provider': '1.0.0' } },

	create: ({ featureServices }) => {
		const serviceConfigProvider = featureServices['service-config-provider'] as ServiceConfigProviderV1;

		const calculatorInfo = new BehaviorSubject<CarlineConfiguration[]>([]);

		let carlineOfertasBase: CarlineWithOfertas[] = [];

		let horariosCallMeNow: {
			horarios: { isOpenNow: boolean | PromiseLike<boolean> };
		} | null = null;

		function filterCarlineBase(
			carlines: CarlineWithOfertas[],
			faContentCarlines: string[],
			faContentMotorizacion: string[],
			faContentGama: string[],
			faContentFinanciacion: string[],
		): CarlineWithOfertas[] {
			let filteredCarlines: CarlineWithOfertas[] = [];

			//Filtramos las carlines si hay en el facontent
			if (Array.isArray(faContentCarlines) && faContentCarlines.length > 0) {
				filteredCarlines = carlines.filter((carline) =>
					faContentCarlines.some((faData) => faData == carline.name || parseInt(faData) == carline.code),
				);
				if (filteredCarlines.length === 0) {
					filteredCarlines = [...carlines];
				}
			} else {
				filteredCarlines = [...carlines];
			}

			//Filtramos las motorizaciones si hay en el facontent
			if (Array.isArray(faContentMotorizacion) && faContentMotorizacion.length > 0) {
				const contentMotorizacion: Record<string, string[]> = {
					Combustion: ['Gasolina', 'Diésel'],
					Mild_Hybrid: ['Gasolina mild Hybrid (etiqueta ECO)'],
					Hibrido_enchufable: ['Híbrido', 'Híbrido enchufable'],
					Electrico: ['Eléctrico'],
				};

				const filteredMotorizaciones = faContentMotorizacion.flatMap((tipo) => contentMotorizacion[tipo] || []);
				carlineOfertasBase = filteredCarlines;
				filteredCarlines = filterBy(filteredMotorizaciones, (oferta) => oferta.configuracion.motorizacion.combustible);
			}

			//Filtramos las gamas si hay en el facontent
			if (Array.isArray(faContentGama) && faContentGama.length > 0) {
				carlineOfertasBase = filteredCarlines;
				filteredCarlines = filterBy(faContentGama, (oferta) => oferta.gama);
			}

			//Filtramos las financiaciones  si hay en el facontent
			if (Array.isArray(faContentFinanciacion) && faContentFinanciacion.length > 0) {
				filteredCarlines = filteredCarlines
					.map((carline) => ({
						...carline,
						ofertas: carline.ofertas
							.map((oferta) => {
								const datosEconomicosFiltrados = oferta.datosEconomicos.filter((dato) =>
									faContentFinanciacion.includes(dato.tipoFinanciacion),
								);
								return datosEconomicosFiltrados.length > 0
									? { ...oferta, datosEconomicos: datosEconomicosFiltrados }
									: null;
							})
							.filter((oferta) => oferta !== null),
					}))
					.filter((carline) => carline.ofertas.length > 0);
			}

			filteredCarlines.forEach((carline) => {
				if (carline.ofertas.length > 1) {
					carline.ofertas.sort((a, b) => b.prioridad - a.prioridad);
				}
			});

			return filteredCarlines;
		}

		function filterBy(
			values: string[],
			getValue: (oferta: Oferta) => string,
			baseCarlines?: CarlineWithOfertas[],
		): CarlineWithOfertas[] {
			const result: CarlineWithOfertas[] = [];
			const addedCarlines = new Set();
			const sourceCarlines = baseCarlines || carlineOfertasBase;

			sourceCarlines.forEach((carline) => {
				const carlineCopy = { ...carline, ofertas: [] as Oferta[] };

				carline.ofertas.forEach((oferta) => {
					if (values.includes(getValue(oferta))) {
						carlineCopy.ofertas.push(oferta);
					}
				});

				if (carlineCopy.ofertas.length > 0 && !addedCarlines.has(carline.id)) {
					result.push(carlineCopy);
					addedCarlines.add(carline.id);
				}
			});

			return result;
		}

		const ofertasFeatureService_2_0_0 = {
			featureService: {
				getOfertas: async (
					faContentData: filtersFaContentOfertas,
					dataService?: ServiceConfigurationV1,
				): Promise<CarlineWithOfertas[]> => {
					const faContentCarlines = faContentData.carlines;
					const faContentMotorizacion = faContentData.motorizaciones;
					const faContentGama = faContentData.gama;
					const faContentFinanciacion = faContentData.financiacion;

					let dataUrl: string | null;
					if (dataService) {
						// SSR
						dataUrl = dataService.urlOrigin + dataService.urlPath + checkTenant(dataService);
					} else {
						dataUrl = getDataUrl();
					}
					//Uncomment next line to get data in pre
					// dataUrl = dataUrl.replace('https://', 'https://pre-')

					// Uncomment to get data in localhost
					/*dataUrl = dataUrl.replace(
							/^https:\/\/(?:pre-)?data-01\.volkswagen\.es\//,
							'http://localhost/vw-vwdata/public/',
						);*/

					const carlines = await fetch(dataUrl + '/v2/getOfertas')
						.then((response) => response.json())
						.then((offers) => offers.carlines);

					//Ordenamos todas las carlines por priordad
					carlines.sort((a: CarlineWithOfertas, b: CarlineWithOfertas) => b.prioridadOffers - a.prioridadOffers);

					carlines.forEach((carline: CarlineWithOfertas) => {
						if (carline.ofertas) {
							carline.ofertas.forEach((oferta: Oferta) => {
								oferta.calculatedFinance = {
									rawResponse: undefined,
									summaries: undefined,
								};
							});
						}
					});

					//Aplicamos los filtros del faContent
					carlineOfertasBase = filterCarlineBase(
						carlines.filter((carline: { ofertas: [] }) => carline.ofertas != null),
						faContentCarlines,
						faContentMotorizacion,
						faContentGama,
						faContentFinanciacion,
					);

					return carlineOfertasBase;
				},

				filterOfertas: (
					gamaFilterValue: string | null,
					motorizacionFilterValue: string | null,
				): CarlineWithOfertas[] => {
					if (!gamaFilterValue && !motorizacionFilterValue) {
						return carlineOfertasBase;
					}

					let ofertasFiltered = carlineOfertasBase;

					if (gamaFilterValue) {
						ofertasFiltered = filterBy([gamaFilterValue], (oferta) => oferta.gama, ofertasFiltered);
					}

					if (motorizacionFilterValue) {
						let combustibles: string[] = [];
						switch (motorizacionFilterValue as FilterTypes) {
							case FilterTypes.ELECTRICOS:
								combustibles = ['Eléctrico'];
								break;
							case FilterTypes.HIBRIDOS_ENCHUFABLES:
								combustibles = ['Híbrido', 'Híbrido enchufable'];
								break;
							case FilterTypes.COMBUSTION:
								combustibles = ['Gasolina', 'Diésel', 'Gasolina mild Hybrid (etiqueta ECO)'];
								break;
						}
						ofertasFiltered = filterBy(
							combustibles,
							(oferta) => oferta.configuracion.motorizacion.combustible,
							ofertasFiltered,
						);
					}

					return ofertasFiltered;
				},

				getDataCalculator: (marketingCode: string): Observable<CarlineConfiguration | null> => {
					const currentCarlineArray = calculatorInfo.getValue() || [];

					const carlineConfiguration = currentCarlineArray.find((item) => item.carlineOfferID === marketingCode);

					if (carlineConfiguration) {
						return of(carlineConfiguration);
					}

					return from(ofertasFeatureService_2_0_0.featureService.dataCalculator(marketingCode)).pipe(
						map((dataCalculator) => {
							const customDataCalculator = {
								carlineOfferID: marketingCode,
								configurations: { ...dataCalculator },
								productConfig: null,
							};
							calculatorInfo.next([...calculatorInfo.getValue(), customDataCalculator]);
							return customDataCalculator;
						}),
						catchError((error) => {
							console.error('Error fetching data:', error);
							return of(null);
						}),
					);
				},

				dataCalculator: async (marketingCode: string): Promise<Configuration> => {
					const {
						urlOrigin,
						urlPath,
						customConfig: { 'X-Api-Key': apiKey } = {},
					} = (serviceConfigProvider.configs['es-oneform-bff-cdn'] || {}) as {
						urlOrigin?: string;
						urlPath?: string;
						customConfig?: { 'X-Api-Key'?: string };
					};

					if (!urlOrigin || !urlPath || !apiKey) {
						throw new Error('Faltan configuraciones para es-oneform-bff-cdn');
					}
					const data = await fetchDataCalculator(urlOrigin, urlPath, marketingCode, apiKey);

					return data;
				},

				updateDataCalculator: (marketingCode: string, productConfig: CalculatorProduct): void => {
					const currentData = calculatorInfo.getValue();

					const updatedData = currentData.map((item: CarlineConfiguration) => {
						if (item.carlineOfferID == marketingCode) {
							return { ...item, productConfig: productConfig };
						}
						return item;
					});

					calculatorInfo.next(updatedData);
				},

				getIsCallMeNowOpen: async (): Promise<boolean> => {
					const dataUrl = getDataUrl();

					if (horariosCallMeNow == null) {
						horariosCallMeNow = await fetch(dataUrl + '/getHorariosCallMeNow')
							.then((response) => response.json())
							.catch(() => false);
					}

					if (horariosCallMeNow?.horarios.isOpenNow) return true;
					else return false;
				},
			},
		};
		return { '2.0.0': () => ofertasFeatureService_2_0_0 };
	},
};
